import PrimaryModal from '../UI/Modal/PrimaryModal';
import { PrimaryButton, SecondaryButton, ErrorButton } from "../UI";
import { useMemo, useRef, useState } from 'react';
import { inviteUser } from '../../utils/APIs';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';
import { toast } from 'react-toastify';
import { useUserContext } from '../../contexts/UserContext';
import Papa from "papaparse";
import Loader from "../UI/Loader/Loader";
import SpinLoader from "../UI/Loader/SpinLoader";
import useComponentVisible from '../UI/ComponentVisible';
import { CheckCircle, EllipsisVertical, XCircle } from 'lucide-react';
import { validateEmail } from '../../utils/helper';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import AgGridWrapper from '../UI/AgGrid';
const CSV_DATA_STATUS = { error: "", success: "" };

const AddOrInviteUserModal = ({ setSelectedAction, selectedOrganization, getOrganizations }) => {
    const { userData } = useUserContext();
    const { content } = useContentfulContext();
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

    const [loading, setLoading] = useState(false);
    const [validateCsvLoading, setValidateCsvLoading] = useState(false);
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});
    const [csvValidation, setCsvValidation] = useState(CSV_DATA_STATUS);
    const [rowData, setRowData] = useState([{ email: "johndow@email.co", role: "Owner" }]);
    const [selectedRowData, setSelectedRowData] = useState({});
    const [deleteIds, setDeleteIds] = useState([]);

    const fileInputRef = useRef("");

    const handleSubmit = async () => {
        try {
            setLoading(true)
            const data = {
                users: rowData.slice(1),
                organizationId: selectedOrganization.org_token,
                organizationName: selectedOrganization.name,
            };
            const result = await inviteUser(userData?.token, data, {
                event_name: ANALYTICS_EVENTS.INVITE_NEW_USER,
                metadata: { invitee_emails: data.users.map(user => user.email) }
            });
            if (result.status) {
                toast.success(content.ADD_OR_INVITE_USER.success.create);
            }
            setSelectedAction("");
        } catch (error) {
            const errMessage = error.message || content.ADD_OR_INVITE_USER.errors.create;
            toast.error(errMessage);
            console.error('Create organization error:', error);
        } finally {
            setLoading(false)
        }
    };

    const downloadCsv = () => {
        const csv = Papa.unparse([
            ["*****"],
            ["-> Stride - Invite Users to organization with specific roles"],
            ["** Please do not delete first 8 rows. (8th row shows user data with sample values) **"],
            ["*****"],
            ["** Role value must be 'Owner', 'Admin' or 'Editor'. **"],
            [" ***** ", " ***** ", " ***** "],
            ["email", "role"],
            ["johndow@email.co", "Owner"],
        ]);
        const filename = `invite-user-to-${selectedOrganization.name}.csv`;

        const link = document.createElement("a");
        const encodedURI = encodeURI(csv);
        const fixedEncodedURI = encodedURI.replaceAll('#', '%23');
        link.setAttribute(
            "href",
            "data:text/csv;charset=utf-8,%EF%BB%BF" + fixedEncodedURI
        );

        link.setAttribute("download", filename);
        link.click();
    };

    const checkValidation = (data) => {
        const newErrors = {};
        data = data.slice(1);

        let isValid = true;
        
        data.forEach((row, rowIndex) => {
            rowIndex = rowIndex + 1 // because we are not checking the first row validation
            if (!newErrors[rowIndex]) newErrors[rowIndex] = {};

            if(!row["email"]) {
                newErrors[rowIndex]["email"] = content.ADD_OR_INVITE_USER.errors.EF131;
                isValid = false;
            } else if(!validateEmail(row["email"])) {
                newErrors[rowIndex]["email"] = content.ADD_OR_INVITE_USER.errors.EF132;
                isValid = false;
            }

            if(!row["role"]) {
                newErrors[rowIndex]["role"] = content.ADD_OR_INVITE_USER.errors.EF133;
                isValid = false;
            } else if(!["Owner", "Admin", "Editor"].includes(row["role"])) {
                newErrors[rowIndex]["role"] = content.ADD_OR_INVITE_USER.notes[2];
                isValid = false;
            }
        })

        if (isValid) {
            setCsvValidation({ error: "", success:content.ADD_EXPERIENCE_MODAL.add_experience_modal.csv_validation_success });
        } else {
            setCsvValidation({ error: content.ADD_EXPERIENCE_MODAL.add_experience_modal.csv_validation_error, success: "" });
        }

        setErrors(newErrors);
        return newErrors;
    }

    const checkCellValidation = (rowIndex, colId, value) => {
        let temp = JSON.parse(JSON.stringify(errors));

        if (!temp[rowIndex]) temp[rowIndex] = {};
        const updateMessage = () => setCsvValidation({ error: content.ADD_EXPERIENCE_MODAL.add_experience_modal.csv_validation_error, success: "" });

        if (colId === "email") {
            if (!value) {
                temp[rowIndex][colId] = content.ADD_OR_INVITE_USER.errors.EF131;
                setErrors(temp)
                updateMessage();
                return;
            } else if (!validateEmail(value)) {
                temp[rowIndex][colId] = content.ADD_OR_INVITE_USER.errors.EF132;
                setErrors(temp)
                updateMessage();
                return;
            }
            temp[rowIndex]["email"] = "";
            delete temp[rowIndex]["email"];
        } else if (colId === "role") {
            if (!value) {
                temp[rowIndex][colId] = content.ADD_OR_INVITE_USER.errors.EF133;
                setErrors(temp)
                updateMessage();
                return;
            } else if (!["Owner", "Admin", "Editor"].includes(value)) {
                temp[rowIndex][colId] = content.ADD_OR_INVITE_USER.notes[2];
                setErrors(temp)
                updateMessage();
                return;
            }
            temp[rowIndex]["role"] = "";
            delete temp[rowIndex]["role"];
        }
        setErrors(temp);
        setCommonErrorMessage(temp)
    }

    function setCommonErrorMessage(errs) {
        // Check if any key in the error object has an error message
        const hasError = Object.values(errs ?? errors).some(
            (errorDetails) => Object.keys(errorDetails).length > 0
        );
        if(!hasError)
            setCsvValidation({ error: "", success: content.ADD_EXPERIENCE_MODAL.add_experience_modal.csv_validation_success })
    }

    const handleOpenThreeDotMenu = (e, params) => {
        e.preventDefault();
        e.stopPropagation();

        // Get the bounding rectangle of the clicked icon
        const buttonRect = e.currentTarget.getBoundingClientRect();

        // Get the modal container's bounding rectangle
        const modalRect = e.currentTarget.closest(".modal-container").getBoundingClientRect();

        // Calculate the position of the menu relative to the modal
        const menuPosition = {
            top: buttonRect.bottom - modalRect.top, // Relative to modal's top
            left: buttonRect.left - modalRect.left - 192, // Relative to modal's left
        };

        // Set the menu position and toggle visibility
        setMenuPosition(menuPosition);
        setSelectedRowData(params);
        setIsComponentVisible(!isComponentVisible);
    }

    const handleSelectCSV = (event) => {
        const file = event.target.files[0];
        setCsvValidation(CSV_DATA_STATUS);
        if (!file) {
            setError(content.ADD_OR_INVITE_USER.errors.select_file);
            return;
        }

        setValidateCsvLoading(true);
        Papa.parse(file, {
            skipEmptyLines: 'greedy',
            encoding: "utf-8",
            complete: function(results) {
                const validRows = results.data
                    .slice(7) // Skip header rows (first 6 rows are headers)
                    .filter(row => row.length >= 2 && row[0] && row[1]) // Filter out any empty rows
                    .map(row => ({
                        email: row[0].trim(),
                        role: row[1].trim()
                    })); // Map to the required format

                setError("");
                setRowData(validRows);
                checkValidation(validRows);
                setValidateCsvLoading(false);
            },
            error: function(error) {
                setError("Error parsing CSV file: " + error.message);
                setValidateCsvLoading(false);
            }
        });
    };

    const columnDefs = useMemo(() => {
        if (rowData.length > 0) {
            // Create columns for each key in the rowData
            const columns = Object.keys(rowData[0]).map((key) => ({
                headerName: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " "), // Replace underscores with spaces in header name
                field: key,
                sortable: true,
                filter: true,
                flex: 1,
                resizable: true,
                suppressSizeToFit: true,
                suppressColumnVirtualisation: true,
                editable: (params) => {
                    if (params.node.rowIndex === 0) return false;
                    return true;
                },
                cellStyle: (params) => {
                    const rowIndex = params.node.rowIndex;
                    // Apply styling if there's an error for the field in that row
                    let classes = { textAlign: "center" };
                    if(errors[rowIndex]?.[key]) {
                        classes.border = "0.8px solid red";
                        classes.backgroundColor = "#ffe6e6";
                    }
                    if(params.node.rowIndex === 0) {
                        classes.pointerEvents = "none";
                        classes.opacity = 0.6;
                        classes.backgroundColor = "#f5f5f5";
                    }
                    return classes;
                },
                cellRenderer: (params) => {
                    return (
                        <div id={params.rowIndex == 1 ? "edit-cell-id" : ""} className="flex items-center justify-between w-full">
                            <p title={params.value} className={`truncate`}>
                                {params?.value}
                            </p>
                        </div>
                    );
                },
            }));

            // Add the three-dot menu column
            columns.push({
                headerName: "", // Empty header for the menu column
                field: "actions",
                maxWidth: 50,
                pinned: "right", // Pin the column to the right
                sortable: false,
                filter: false,
                resizable: false,
                suppressSizeToFit: true,
                cellRenderer: (params) => {
                    return (
                        <div 
                            id={params?.rowIndex == 0 ? "first-row-action-of-preview-id" : ""}
                            onClick={(e) => handleOpenThreeDotMenu(e, params)}
                            className={`flex justify-center items-center h-full w-full`}
                        >
                            <EllipsisVertical
                                size={40}
                                color="black"
                                className={`cursor-pointer`}
                            />
                        </div>
                    );
                },
            });

            // Add a "Status" column for validation icons at the start
            columns.unshift({
                headerName: "Status",
                field: "status",
                maxWidth: 90,
                sortable: false,
                pinned: "left",
                filter: false,
                resizable: true,
                suppressSizeToFit: true,
                suppressColumnVirtualisation: true,
                headerClass: "center-header ag-align-center-header w-full",
                cellStyle: { textAlign: "center" }, // Center-align the row numbers
                cellRenderer: (params) => {
                    const rowIndex = params.node.rowIndex;
                    const hasError =
                        errors[rowIndex] &&
                        Object.keys(errors[rowIndex]).length > 0;
                    return (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                height: "100%",
                            }}
                        >
                            {hasError ? (
                                <XCircle color="red" size={24} />
                            ) : (
                                <CheckCircle color="green" size={24} />
                            )}
                        </div>
                    );
                },
            });

            columns.unshift({
                headerName: "#", // Header for the row number column
                field: "rowNumber",
                maxWidth: 45,
                pinned: "left", // Pin the column to the left
                sortable: false,
                filter: false,
                resizable: false,
                suppressSizeToFit: true,
                suppressColumnVirtualisation: true,
                cellStyle: { textAlign: "center" }, // Center-align the row numbers
                valueGetter: (params) => params.node.rowIndex + 1, // Generate row numbers dynamically
            });

            columns.unshift({
                headerName: "", // Header for the row number column
                field: "checkbox",
                maxWidth: 35,
                pinned: "left", // Pin the column to the left
                checkboxSelection: (params) => params.node.rowIndex === 0 ? false : true,
                headerCheckboxSelection: true,
                suppressMovable: true,
                resizable: false,
                suppressSizeToFit: true,
                suppressColumnVirtualisation: true,
                cellStyle: { textAlign: "center" },
            });

            return columns;
        }
        return [];
    }, [rowData, errors]);

    const autoSizeStrategy = useMemo(() => {
        return {
            type: "fitGridWidth",
        };
    }, [rowData]);

    const handleOnRowSelected = (event) => {
        const selectedNodes = event.api.getSelectedNodes();
        const indexes = selectedNodes.map((node) => node.rowIndex);
        setDeleteIds(indexes)
    }

    const onGridReady = (params) => {
        params.api.sizeColumnsToFit();
    };

    const onCellValueChanged = (event) => {
        const { data, rowIndex } = event;
        setRowData((prevData) => {
            const updatedData = [...prevData];
            updatedData[rowIndex] = data; // Update the changed row
            return updatedData;
        });
    };

    const onCellEditingStopped = (event) => {
        setError("");
        checkCellValidation(event.rowIndex, event.column.colId, event.value);
    };

    const onSortChanged = (params) => {
        const sortedData = [];
        params.api.forEachNodeAfterFilterAndSort((node) => {
            sortedData.push(node.data);
        });

        checkValidation(sortedData);
        setRowData(sortedData);
    };

    const onCellClicked = (params) => {
        const { node, colDef } = params;
        const rowIndex = node.rowIndex;
        const field = colDef.field;

        if (errors[rowIndex]?.[field]) {
            setError(`Error: ${errors[rowIndex][field]}`);
        } else {
            setError("");
        }
    };

    const handleAddDuplicateRow = () => {
        const newRow = { ...rowData[selectedRowData.rowIndex] };
        const updatedData = [...rowData];
        updatedData.splice(selectedRowData.rowIndex + 1, 0, newRow);
        setRowData(updatedData);
        setIsComponentVisible(false);
        checkValidation(updatedData);
    };

    const handleAddRow = () => {
        const updatedData = [...rowData];
        const newItem = {
            ...Object.keys(selectedRowData.data).reduce((acc, key) => {
                acc[key] = null; // Set all fields to null
                return acc;
            }, {}),
        };
        updatedData.splice(selectedRowData.rowIndex + 1, 0, newItem);
        setRowData(updatedData);
        setIsComponentVisible(false);
        checkValidation(updatedData);
    }

    const handleDeleteRow = () => {
        if(deleteIds.length > 0) {
            const temp = deleteIds.filter((x) => x != 0)
            const indexesSet = new Set(temp)
            const updatedData = rowData.filter((_, index) => !indexesSet.has(index))
            setRowData(updatedData)
            setIsComponentVisible(false);
            checkValidation(updatedData);
            setDeleteIds([])
            return;
        }
        if(selectedRowData.rowIndex == 0) return;
        const updatedData = [...rowData];
        updatedData.splice(selectedRowData.rowIndex, 1);
        setRowData(updatedData);
        setIsComponentVisible(false);
        checkValidation(updatedData);
    }

    return (
        <PrimaryModal
            title={selectedOrganization?.display_name}
            hideModal={setSelectedAction}
            widthClass="max-w-4xl"
        >
            {loading && <Loader />}
            {validateCsvLoading && (
                <div className="rounded-lg absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                    <SpinLoader />
                </div>
            )}

            <div className="p-6 space-y-4">
                <p className="flex items-center text-black">
                    {content.ADD_OR_INVITE_USER.download_csv_note}
                </p>

                <a
                    id="download-sample-csv-btn-id"
                    className="text-primary cursor-pointer m-0 p-0"
                    onClick={downloadCsv}
                >
                    {"Download sample"}
                </a>

                <div>
                    <input
                        disabled={
                            loading ||
                            validateCsvLoading
                        }
                        id='upload-csv-file-id'
                        type="file"
                        ref={fileInputRef}
                        onChange={handleSelectCSV}
                        accept=".csv"
                    />
                </div>

                <div className="space-y-2 mt-4 h-[48px]">
                    {!error && !csvValidation.error && <div className="flex items-center h-full">
                        <p className="text-sm text-[#27942B]">
                            {csvValidation.success}
                        </p>
                    </div>}
                    <div className="flex flex-col">
                        <p className="text-sm text-red-500">
                            {csvValidation.error}
                        </p>
                        <p className="text-sm text-red-500">{error}</p>
                    </div>
                </div>

                <div className={"ag-theme-quartz"} style={{ height: "100%", width: "100%" }} >
                    <div
                        id="experience-preview-table-id"
                        className="ag-theme-quartz z-50"
                        style={{ width: "100%" }}
                    >
                        <AgGridWrapper
                            tableId="add-or-invite-user-table"
                            rowData={rowData}
                            columnDefs={columnDefs}
                            autoSizeStrategy={autoSizeStrategy}
                            rowSelection="multiple"
                            suppressRowClickSelection
                            onRowSelected={handleOnRowSelected}
                            onGridReady={onGridReady}
                            onSortChanged={onSortChanged}
                            onCellValueChanged={onCellValueChanged}
                            onCellClicked={onCellClicked}
                            onCellEditingStopped={onCellEditingStopped}
                            stopEditingWhenCellsLoseFocus={true}
                        />
                    </div>

                    <div
                        ref={ref}
                        className={`absolute z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${isComponentVisible ? "" : "hidden"}`}
                        style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
                    >
                        <div class="py-1">
                            <a id="add-duplicate-of-first-row-btn-id" onClick={handleAddDuplicateRow} href="#" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" tabIndex="-1" >
                                {content.ADD_OR_INVITE_USER.duplicate_row_btn}
                            </a>
                            <a onClick={handleAddRow} href="#" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" tabIndex="-1">
                                {content.ADD_OR_INVITE_USER.add_new_row_btn}
                            </a>
                            {selectedRowData.rowIndex !== 0 && <a onClick={handleDeleteRow} href="#" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100" tabIndex="-1">
                                {content.ADD_OR_INVITE_USER.delete_btn}
                            </a>}
                        </div>
                    </div>

                    <div className="mt-2">
                        <p className="text-sm italic">{content.ADD_OR_INVITE_USER.notes[0]}</p>
                        <p className="text-sm italic">{content.ADD_OR_INVITE_USER.notes[1]}</p>
                        <p className="text-sm italic">{content.ADD_OR_INVITE_USER.notes[2]}</p>
                    </div>

                    <div className="flex items-center justify-end space-x-4 sticky bottom-0 bg-white px-6 py-2 pb-4 shadow-md">
                        <div className="space-x-4">
                            {deleteIds.length > 0 && (
                                <ErrorButton handleClick={() => handleDeleteRow(deleteIds)}>
                                    {content.ADD_OR_INVITE_USER.delete_btn}
                                </ErrorButton>
                            )}
                            <SecondaryButton handleClick={() => setSelectedAction("")}>
                                {content.ADD_OR_INVITE_USER.cancel_btn}
                            </SecondaryButton>
                            <PrimaryButton
                                id={"submit-btn-id"}
                                disable={loading || validateCsvLoading || !!csvValidation.error}
                                handleClick={handleSubmit}
                            >
                                {content.ADD_OR_INVITE_USER.submit_btn}
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            </div>

        </PrimaryModal>
    );
};

export default AddOrInviteUserModal;
